import { useState, useRef } from 'react';
import { MapPin, Clock, Car, Palmtree, ShoppingCart, Dumbbell, Coffee, GraduationCap, Building2, Heart, Landmark } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { trackEvent } from '@/lib/analytics';
import { CustomStyledMap, type AmenityMarker, type MapRef } from '@/components/CustomStyledMap';

const nearbyAmenities = [
  { icon: ShoppingCart, label: 'Grocery', distance: '2 min walk' },
  { icon: Dumbbell, label: 'Gym', distance: '3 min walk' },
  { icon: Coffee, label: 'Cafés', distance: '5 min walk' },
  { icon: GraduationCap, label: 'Schools', distance: '8 min drive' },
];

const commutes = [
  { icon: Car, label: 'Airport', time: '10 min', markerTitle: 'Owen Roberts International Airport' },
  { icon: Heart, label: 'Hospital', time: '10 min', markerTitle: 'Cayman Islands Hospital' },
  { icon: Palmtree, label: 'Seven Mile Beach', time: '12 min', markerTitle: 'Seven Mile Beach' },
  { icon: Landmark, label: 'National Museum', time: '12 min', markerTitle: 'Cayman Islands National Museum' },
  { icon: MapPin, label: 'Camana Bay', time: '15 min', markerTitle: 'Camana Bay' },
];

// Grand Harbour area bounds for highlighting
// Calculated based on provided coordinates to encompass the neighborhood
const grandHarbourBounds = {
  north: 19.297, // Slightly north of center to include the area
  south: 19.278, // South of Truman Bodden Sports Complex
  east: -81.340, // East of center
  west: -81.385, // West of Truman Bodden Sports Complex
};

// Use a single pin icon for all amenities (simple pin style)
const defaultPinIcon = '/map-icons/grocery-pin.svg';

// The Pearl site marker
const thePearlMarker: AmenityMarker = {
  position: { lat: 19.282988762255393, lng: -81.34556344978195 },
  icon: '/map-icons/pearl-pin.svg',
  title: 'The Pearl at Grand Harbour',
  type: 'pearl',
  infoWindow: {
    title: 'The Pearl at Grand Harbour',
    content: 'Lagoon-side luxury living in Grand Harbour',
  },
};

// Nearby amenity markers - all use the same simple pin icon
const amenityMarkers: AmenityMarker[] = [
  // Grocery/Retail
  {
    position: { lat: 19.28174622272351, lng: -81.34625066736119 },
    icon: defaultPinIcon,
    title: 'Grand Harbour Shopping Centre',
    type: 'grocery',
    distance: '2 min walk',
    infoWindow: {
      title: 'Grand Harbour Shopping Centre',
      content: 'Shopping and retail center',
    },
  },
  {
    position: { lat: 19.282156193391423, lng: -81.34638493737697 },
    icon: defaultPinIcon,
    title: 'Health Care Pharmacy',
    type: 'grocery',
    distance: '2 min walk',
    infoWindow: {
      title: 'Health Care Pharmacy',
      content: 'Pharmacy and health services',
    },
  },
  // Gyms/Fitness
  {
    position: { lat: 19.28639839062961, lng: -81.34968506441352 },
    icon: defaultPinIcon,
    title: "King's Sport Centre",
    type: 'gym',
    distance: '3 min walk',
    infoWindow: {
      title: "King's Sport Centre",
      content: 'Fitness and sports facility',
    },
  },
  {
    position: { lat: 19.287826607227633, lng: -81.37247284272408 },
    icon: defaultPinIcon,
    title: 'Pickleball Cayman',
    type: 'gym',
    distance: '5 min drive',
    infoWindow: {
      title: 'Pickleball Cayman',
      content: 'Pickleball courts and facilities',
    },
  },
  {
    position: { lat: 19.27893540277211, lng: -81.3829333701534 },
    icon: defaultPinIcon,
    title: 'Truman Bodden Sports Complex',
    type: 'gym',
    distance: '8 min drive',
    infoWindow: {
      title: 'Truman Bodden Sports Complex',
      content: 'Multi-sport complex and facilities',
    },
  },
  // Cafés/Restaurants
  {
    position: { lat: 19.28199758522619, lng: -81.3458504463085 },
    icon: defaultPinIcon,
    title: "Hurley's",
    type: 'cafe',
    distance: '2 min walk',
    infoWindow: {
      title: "Hurley's",
      content: 'Restaurant and café',
    },
  },
  {
    position: { lat: 19.282228799895584, lng: -81.345450044992 },
    icon: defaultPinIcon,
    title: "Salty's Sports Bar",
    type: 'cafe',
    distance: '2 min walk',
    infoWindow: {
      title: "Salty's Sports Bar",
      content: 'Sports bar and restaurant',
    },
  },
  // Schools
  {
    position: { lat: 19.28233864044324, lng: -81.34606344484796 },
    icon: defaultPinIcon,
    title: 'Hope Academy',
    type: 'school',
    distance: '2 min walk',
    infoWindow: {
      title: 'Hope Academy',
      content: 'Private school',
    },
  },
  {
    position: { lat: 19.279705010454535, lng: -81.37536987102668 },
    icon: defaultPinIcon,
    title: 'Triple C School',
    type: 'school',
    distance: '8 min drive',
    infoWindow: {
      title: 'Triple C School',
      content: 'Private school',
    },
  },
  // Services
  {
    position: { lat: 19.282076140029023, lng: -81.345872127208 },
    icon: defaultPinIcon,
    title: 'Spa to Go',
    type: 'service',
    distance: '2 min walk',
    infoWindow: {
      title: 'Spa to Go',
      content: 'Spa and wellness services',
    },
  },
  {
    position: { lat: 19.289414856336748, lng: -81.38027920644755 },
    icon: defaultPinIcon,
    title: 'Cayman Islands Hospital',
    type: 'service',
    distance: '10 min drive',
    infoWindow: {
      title: 'Cayman Islands Hospital',
      content: 'Main hospital and medical center',
    },
  },
  {
    position: { lat: 19.29460521235949, lng: -81.38277724291619 },
    icon: defaultPinIcon,
    title: 'Cayman Islands National Museum',
    type: 'service',
    distance: '12 min drive',
    infoWindow: {
      title: 'Cayman Islands National Museum',
      content: 'Cultural and historical museum',
    },
  },
];

// Commute destination markers - all use the same simple pin icon
const commuteMarkers: AmenityMarker[] = [
  {
    position: { lat: 19.29661989517206, lng: -81.35768279139828 },
    icon: defaultPinIcon,
    title: 'Owen Roberts International Airport',
    type: 'airport',
    time: '10 min',
    infoWindow: {
      title: 'Owen Roberts International Airport',
      content: 'Main international airport',
    },
  },
  {
    position: { lat: 19.289414856336748, lng: -81.38027920644755 },
    icon: defaultPinIcon,
    title: 'Cayman Islands Hospital',
    type: 'service',
    time: '10 min',
    infoWindow: {
      title: 'Cayman Islands Hospital',
      content: 'Main hospital and medical center',
    },
  },
  {
    position: { lat: 19.336582860814506, lng: -81.38059680980255 },
    icon: defaultPinIcon,
    title: 'Seven Mile Beach',
    type: 'beach',
    time: '12 min',
    infoWindow: {
      title: 'Seven Mile Beach',
      content: 'World-renowned beach destination',
    },
  },
  {
    position: { lat: 19.29460521235949, lng: -81.38277724291619 },
    icon: defaultPinIcon,
    title: 'Cayman Islands National Museum',
    type: 'service',
    time: '12 min',
    infoWindow: {
      title: 'Cayman Islands National Museum',
      content: 'Cultural and historical museum',
    },
  },
  {
    position: { lat: 19.323151407028544, lng: -81.37822863021778 },
    icon: defaultPinIcon,
    title: 'Camana Bay',
    type: 'destination',
    time: '15 min',
    infoWindow: {
      title: 'Camana Bay',
      content: 'Shopping, dining, and entertainment district',
    },
  },
];

export const LocationMap = () => {
  const mapRef = useRef<MapRef | null>(null);
  const [highlightedCommute, setHighlightedCommute] = useState<string | null>(null);
  const [highlightedAmenity, setHighlightedAmenity] = useState<string | null>(null);
  const [visibleMarkerTitles, setVisibleMarkerTitles] = useState<string[]>([
    thePearlMarker.title, // Always show The Pearl
  ]);

  const handleMapInteract = () => {
    trackEvent('map_interact', { location: 'location_section' });
  };

  const handleAmenityClick = (amenityType: 'grocery' | 'cafe' | 'gym' | 'school') => {
    console.log('🚀 AMENITY CLICK:', amenityType);
    trackEvent('amenity_click', { amenity_type: amenityType, location: 'location_section' });
    
    if (!mapRef.current) {
      console.error('❌ Map ref not ready');
      return;
    }

    // Find all markers of this type
    const relevantMarkers = amenityMarkers.filter((marker) => {
      if (amenityType === 'grocery') return marker.type === 'grocery';
      if (amenityType === 'cafe') return marker.type === 'cafe';
      if (amenityType === 'gym') return marker.type === 'gym';
      if (amenityType === 'school') return marker.type === 'school';
      return false;
    });

    console.log(`📍 Found ${relevantMarkers.length} ${amenityType} markers`);

    if (relevantMarkers.length === 0) {
      console.warn(`⚠️ No ${amenityType} markers found`);
      return;
    }

    setHighlightedAmenity(amenityType);

    // Show The Pearl and all relevant markers
    const markerTitles = [thePearlMarker.title, ...relevantMarkers.map(m => m.title)];
    setVisibleMarkerTitles(markerTitles);

    // Show all markers
    relevantMarkers.forEach((marker) => {
      mapRef.current?.showMarker(marker.title);
    });

    // Zoom to show The Pearl and all relevant markers
    setTimeout(() => {
      if (mapRef.current && relevantMarkers.length > 0) {
        // Create array of positions including The Pearl and all relevant markers
        const positions = [
          thePearlMarker.position,
          ...relevantMarkers.map(m => m.position)
        ];

        // Use zoomToShowMultiple to fit all markers
        mapRef.current.zoomToShowMultiple(positions);

        // Reset after 8 seconds
        setTimeout(() => {
          setHighlightedAmenity(null);
          relevantMarkers.forEach((marker) => {
            mapRef.current?.hideMarker(marker.title);
          });
          setVisibleMarkerTitles([thePearlMarker.title]);
          mapRef.current?.resetView();
        }, 8000);
      }
    }, 200);
  };

  const handleCommuteClick = (commuteLabel: string, markerTitle: string, commuteTime: string) => {
    console.log('🚀 COMMUTE CLICK:', { commuteLabel, markerTitle, commuteTime });
    trackEvent('commute_click', { destination: commuteLabel, location: 'location_section' });
    
    // Find the commute marker by title
    const commuteMarker = commuteMarkers.find((c) => c.title === markerTitle);
    console.log('📍 Commute marker found:', commuteMarker);
    if (!commuteMarker) {
      console.error('❌ Commute marker not found:', markerTitle);
      console.log('Available commute markers:', commuteMarkers.map(c => c.title));
      return;
    }
    
    console.log('🗺️ Map ref status:', { hasMapRef: !!mapRef.current });
    if (!mapRef.current) {
      console.error('❌ Map ref not ready');
      return;
    }

    console.log('✅ Setting highlighted commute:', commuteLabel);
    setHighlightedCommute(commuteLabel);

    // Show the destination marker immediately
    console.log('👁️ Setting visible markers:', [thePearlMarker.title, markerTitle]);
    setVisibleMarkerTitles([thePearlMarker.title, markerTitle]);
    
    // Show marker and zoom immediately (don't wait for state)
    if (mapRef.current) {
      console.log('📍 Calling showMarker for:', markerTitle);
      mapRef.current.showMarker(markerTitle);
      
      // Small delay to ensure marker is rendered before zooming
      setTimeout(() => {
        console.log('🔍 Zooming to show both locations');
        console.log('   The Pearl:', thePearlMarker.position);
        console.log('   Destination:', commuteMarker.position);
        
        if (mapRef.current) {
          // Zoom to show both The Pearl and destination
          mapRef.current.zoomToShowBoth(
            thePearlMarker.position,
            commuteMarker.position
          );
          console.log('✅ Zoom completed');

          // Open info window automatically after zoom
          setTimeout(() => {
            console.log('📋 Opening info window for:', markerTitle);
            mapRef.current?.openInfoWindow(markerTitle);
          }, 400);

          // Reset after 8 seconds - close info window, hide the destination marker and reset view
          setTimeout(() => {
            console.log('🔄 Resetting view');
            mapRef.current?.closeInfoWindow();
            setHighlightedCommute(null);
            mapRef.current?.hideMarker(markerTitle);
            setVisibleMarkerTitles([thePearlMarker.title]); // Only show The Pearl again
            mapRef.current?.resetView();
          }, 8000);
        } else {
          console.error('❌ Map ref lost during timeout');
        }
      }, 200);
    } else {
      console.error('❌ Map ref is null');
    }
  };

  return (
    <section id="location" className="py-20 lg:py-28 scroll-mt-20">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-16">
          <h2 className="font-display text-3xl lg:text-5xl font-semibold text-foreground mb-6">
            The 5-Minute Life
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed">
            In Grand Harbour with immediate access to everyday essentials; minutes to the Seven Mile corridor.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-16 items-start">
          {/* Custom Styled Google Map */}
          <Card className="shadow-luxury border">
            <CardContent className="p-0">
              <div className="relative h-[400px] lg:h-[500px] rounded-lg overflow-hidden">
                {import.meta.env.VITE_GOOGLE_MAPS_API_KEY ? (
                  <CustomStyledMap
                    apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY}
                    center={thePearlMarker.position} // Center on The Pearl
                    zoom={14}
                    markers={[
                      thePearlMarker,
                      ...amenityMarkers,
                      ...commuteMarkers.filter(
                        (commute) => !amenityMarkers.some((amenity) => amenity.title === commute.title)
                      ),
                    ]}
                    visibleMarkers={visibleMarkerTitles}
                    onMapReady={(ref) => {
                      mapRef.current = ref;
                    }}
                    themeColors={{
                      water: '#e8f4f8', // Light turquoise for Caribbean water
                      land: '#f5f5f5', // Light beige/gray for land
                      roads: '#e0e0e0', // Very light gray for roads
                    }}
                  />
                ) : (
                  // Fallback placeholder if API key not configured
                  <div
                    className="relative bg-muted h-full rounded-lg overflow-hidden cursor-pointer flex items-center justify-center"
                    onClick={handleMapInteract}
                  >
                    <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-brand-teal/20 to-brand-navy/20">
                      <div className="text-center p-8">
                        <MapPin className="w-16 h-16 text-brand-teal mx-auto mb-4" />
                        <p className="font-display text-xl text-foreground font-semibold mb-2">
                          Grand Harbour, Grand Cayman
                        </p>
                        <p className="text-muted-foreground text-sm">
                          Strategic location • Easy access • Connected community
                        </p>
                      </div>
                    </div>
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                      <div className="relative">
                        <div className="absolute inset-0 animate-ping">
                          <div className="w-8 h-8 rounded-full bg-primary/50" />
                        </div>
                        <MapPin className="relative w-8 h-8 text-primary fill-primary drop-shadow-lg" />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Location Benefits */}
          <div className="space-y-10">
            {/* Nearby Amenities */}
            <div>
              <h3 className="font-display text-2xl font-semibold text-foreground mb-6 flex items-center gap-2">
                <Clock className="w-6 h-6 text-primary" />
                Walking Distance
              </h3>
              <div className="grid grid-cols-2 gap-4">
                {nearbyAmenities.map((amenity) => {
                  const Icon = amenity.icon;
                  const amenityType = amenity.label.toLowerCase() === 'grocery' ? 'grocery' :
                                     amenity.label.toLowerCase() === 'cafés' ? 'cafe' :
                                     amenity.label.toLowerCase() === 'gym' ? 'gym' :
                                     amenity.label.toLowerCase() === 'schools' ? 'school' : null;
                  const isHighlighted = highlightedAmenity === amenityType;
                  
                  return (
                    <button
                      key={amenity.label}
                      type="button"
                      onClick={() => {
                        console.log('Badge clicked:', amenity.label, amenityType);
                        if (amenityType) {
                          handleAmenityClick(amenityType);
                        }
                      }}
                      className={`text-left transition-all ${
                        isHighlighted ? 'scale-[1.02]' : ''
                      }`}
                    >
                      <Badge
                        variant="outline"
                        className={`justify-start p-3 text-sm font-normal border transition-all w-full ${
                          isHighlighted
                            ? 'border-primary bg-primary/10 shadow-lg'
                            : 'border-primary/20 hover:bg-primary/5 hover:border-primary/50'
                        }`}
                      >
                        <Icon className="w-5 h-5 mr-2 text-primary" />
                        <div className="flex flex-col items-start">
                          <span className="font-medium text-foreground">{amenity.label}</span>
                          <span className="text-xs text-muted-foreground">{amenity.distance}</span>
                        </div>
                      </Badge>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Commute Times */}
            <div>
              <h3 className="font-display text-2xl font-semibold text-foreground mb-6 flex items-center gap-2">
                <Car className="w-6 h-6 text-primary" />
                Quick Commutes
              </h3>
              <div className="space-y-4">
                {commutes.map((commute) => {
                  const Icon = commute.icon;
                  const isHighlighted = highlightedCommute === commute.label;
                  
                  return (
                    <div
                      key={commute.label}
                      onClick={() => handleCommuteClick(commute.label, commute.markerTitle || commute.label, commute.time)}
                      className={`flex items-center justify-between p-4 rounded-lg bg-secondary/50 border transition-all cursor-pointer ${
                        isHighlighted 
                          ? 'border-primary bg-primary/10 shadow-lg scale-[1.02]' 
                          : 'border-border hover:border-primary/50 hover:bg-primary/5'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <Icon className="w-5 h-5 text-primary" />
                        <span className="font-medium text-foreground">{commute.label}</span>
                      </div>
                      <span className="text-sm font-semibold text-primary">{commute.time}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Location Highlights */}
            <Card className="bg-primary/5 border-primary/20">
              <CardContent className="p-6">
                <h4 className="font-display text-lg font-semibold text-foreground mb-3">
                  Why Grand Harbour?
                </h4>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span>Established neighborhood with mature infrastructure</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span>Walking distance to daily essentials and services</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span>Central location between airport and Seven Mile Beach</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-1">•</span>
                    <span>Strong rental demand from professionals and families</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};
